import {
	Entypo,
	FontAwesome,
	Ionicons,
	MaterialCommunityIcons,
	MaterialIcons
} from "@expo/vector-icons";
import { Asset } from "expo-asset";
import * as Font from "expo-font";
import * as Localization from "expo-localization";
import * as SplashScreen from "expo-splash-screen";
import React, { useEffect, useState } from "react";
import { Image, LogBox, StatusBar, Text, UIManager, View } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { enableScreens } from "react-native-screens";
import { Provider } from "react-redux";
import { fetchGetCachedImages } from "./src/actions";
import { SET_LANG } from "./src/actions/types";
import { Images, setCachedImages } from "./src/assets/Images";
import * as config from "./src/config/i18n";
import { fetch } from "./src/middleware/api";
import AppNavigationContainer from "./src/navigation/AppNavigationContainer";
import i18n from "./src/services/i18n";
import configureStore from "./src/store/configure-store";
import commonStyles, {
	FONT,
	FONT_BOLD,
	FONT_LIGHT,
	isAndroid,
	isElectron,
	isNative,
	isWeb
} from "./src/styles/commonStyles";

SplashScreen.preventAutoHideAsync();

//Optimize memory usage and performance
enableScreens();

if (isWeb) {
	// Add Google maps api
	const script = document.createElement("script");
	script.src = `https://maps.googleapis.com/maps/api/js?key=${
		__DEV__ && !isElectron
			? "AIzaSyCULbeeNoYJep_Qzb_9nmf_vJkzA467Oeg"
			: "AIzaSyDoTNc45o-X3V5iSYUqjKgr2tkv4Wh_9sc"
	}`;
	script.async = true;
	document.body.appendChild(script);
	const base = document.createElement("base");
	base.href = "./";
	document.head.appendChild(base);
}

StatusBar.setBarStyle("light-content");

if (isAndroid) {
	if (UIManager.setLayoutAnimationEnabledExperimental) {
		UIManager.setLayoutAnimationEnabledExperimental(true);
	}
}

LogBox.ignoreLogs([
	"Sending",
	"ReactImageView",
	"VirtualizedLists should never be nested",
	"Warning: componentWillReceiveProps has been renamed",
	"Warning: componentWillMount has been renamed",
	"Constants.platform.ios.model has been deprecated in favor of expo-device's Device.modelName property. This API will be removed in SDK 45."
]);

const store = configureStore();

function cacheImages(images) {
	return images.map(image => {
		if (typeof image === "string") {
			return Image.prefetch(image);
		} else {
			return Asset.fromModule(
				isElectron ? image.default : image
			).downloadAsync();
		}
	});
}

function cacheFonts(fonts) {
	return fonts.map(font => Font.loadAsync(font));
}

export default function App() {
	const [isReady, setReady] = useState(false);
	const { flex1, content } = commonStyles;

	const _loadAssetsAsync = async () => {
		await i18n.init();

		// Disable font scaling for Android
		Text.defaultProps = Text.defaultProps || {};
		Text.defaultProps.allowFontScaling = false;

		// Init firebase. iOS was excluded here, which is why Web Push never worked
		// on the iOS PWA: `firebase.messaging()` then threw on an uninitialised
		// app. Safari 16.4+ supports Web Push once the PWA is installed, and
		// `PushNotifications.web` guards the unsupported browsers on its own.
		if (isWeb && !isElectron) {
			require("./firebase/config").firebaseInit();
		}

		let cachedImages;
		// Retrieve cached images
		try {
			cachedImages = await fetch(fetchGetCachedImages());
			setCachedImages(cachedImages);
		} catch (error) {
			console.error(error);
		}

		const lng = Localization.getLocales()?.[0];
		store.dispatch({
			type: SET_LANG,
			lang: config.supportedLocales[i18n.locale]
				? i18n.locale
				: config.fallback,
			country: lng?.regionCode ?? "FR"
		});

		const imageAssets = cacheImages(Object.values(Images));

		const fontAssets = cacheFonts([
			{
				fontBold: isElectron ? FONT_BOLD.default : FONT_BOLD,
				fontLight: isElectron ? FONT_LIGHT.default : FONT_LIGHT,
				font: isElectron ? FONT.default : FONT
			},
			FontAwesome.font,
			MaterialCommunityIcons.font,
			MaterialIcons.font,
			Ionicons.font,
			Entypo.font
		]);
		await Promise.all([...imageAssets, ...fontAssets]);

		setReady(true);
	};

	useEffect(() => {
		(async () => {
			await _loadAssetsAsync();
		})();
	}, []);

	if (!isReady) {
		return null;
	}

	return (
		<View style={[flex1, { backgroundColor: "rgba(0,0,0,0.3)" }]}>
			<SafeAreaProvider style={content}>
				<Provider store={store}>
					<AppNavigationContainer />
				</Provider>
			</SafeAreaProvider>
		</View>
	);
}
